import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CardActions
} from '@mui/material';
import { 
  Save as SaveIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { organisationService } from '../../services/serviceFactory';
import { Organisation } from '../../services/interfaces';

const OrganisationProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Organisation>>({
    name: '',
    description: '',
    city: '',
    street: '',
    zipCode: '',
    country: '',
  });

  useEffect(() => {
    fetchOrganisation();
  }, []);
// In your fetchOrganisation function in OrganisationProfile.tsx
const fetchOrganisation = async () => {
    if (!user?.organisationId) {
      setError(t('organisation.profile.noOrganisation'));
      setLoading(false);
      return;
    }
  
    try {
      setLoading(true);
      const data = await organisationService.getOrganisation(user.organisationId);
      
      if (data) {
        setOrganisation(data);
        setFormData({
          name: data.name,
          description: data.description || '',
          city: data.city || '',
          street: data.street || '',
          zipCode: data.zipCode || '',
          country: data.country || '',
        });
      } else {
        setError(t('organisation.profile.notFound'));
      }
      
    } catch (err) {
      console.error('Failed to fetch organisation:', err);
      setError(t('organisation.profile.errorLoading'));
    } finally {
      setLoading(false);
    }
  };
  
  // And in your handleSubmit function:
  const handleSubmit = async () => {
    if (!organisation) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Update only the fields that are editable
      const updatedOrganisation = {
        ...organisation,
        description: formData.description,
        city: formData.city,
        street: formData.street,
        zipCode: formData.zipCode,
        country: formData.country,
      };
      
      const result = await organisationService.updateOrganisation(updatedOrganisation);
      
      if (result) {
        setOrganisation(updatedOrganisation);
        setSuccess(t('organisation.profile.saveSuccess'));
      } else {
        setError(t('organisation.profile.saveError'));
      }
    } catch (err) {
      console.error('Failed to update organisation:', err);
      setError(t('organisation.profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organisation) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error || t('organisation.profile.notFound')}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        {t('organisation.profile.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title={t('organisation.profile.generalInfo')}
              avatar={<BusinessIcon />}
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t('organisation.profile.name')}
                    value={formData.name}
                    fullWidth
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label={t('organisation.profile.description')}
                    value={formData.description}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                {organisation.taxNumber && (
                  <Grid item xs={12}>
                    <TextField
                      label={t('organisation.profile.taxNumber')}
                      value={organisation.taxNumber}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title={t('organisation.profile.addressInfo')}
              avatar={<LocationIcon />}
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="street"
                    label={t('organisation.profile.street')}
                    value={formData.street}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="city"
                    label={t('organisation.profile.city')}
                    value={formData.city}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="zipCode"
                    label={t('organisation.profile.zipCode')}
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="country"
                    label={t('organisation.profile.country')}
                    value={formData.country}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? t('saving') : t('save')}
        </Button>
      </Box>
    </Box>
  );
};

export default OrganisationProfile;